import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Calendar, Send, X } from "lucide-react";
import MobPopover from "./MobPopover";
import VehicleCrewRows from "./VehicleCrewRows";
import { smartTitleFromMob } from "../lib/mobTitle";

// --- date helpers ---
const d2 = (n) => String(n).padStart(2, "0");
const fmtIso = (d) => `${d.getFullYear()}-${d2(d.getMonth() + 1)}-${d2(d.getDate())}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(d.getDate() + n); return x; };
const startOfWeek = (d) => {
  const x = new Date(d);
  x.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // Monday start
  return x;
};
const SHORT_DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

// Overlay colors for day-status cells.
// User-facing menu options: PTO, Sick, Personal, Training / Shop.
// `travel`, `warehouse`, `holiday`, `shop` are kept for backward
// compatibility with assignments saved before May 2026 — they still render
// with their original color but no longer appear in the picker menu.
const STATUS_STYLE = {
  pto:        { bg: "#FED7AA", label: "PTO" },              // light orange (orange-200)
  sick:       { bg: "#FECACA", label: "Sick" },             // light red (red-200)
  personal:   { bg: "#FEE2E2", label: "Personal" },         // lighter red (red-100)
  training:   { bg: "#BBF7D0", label: "Training / Shop" },  // light green (green-200)
  travel:     { bg: "#FFC000", label: "Travel / Out of Town" },
  warehouse:  { bg: "#DDD9C4", label: "Warehouse" },
  holiday:    { bg: "var(--cell-holiday-bg, #E5E7EB)", label: "Holiday" },  // light gray / dark tint in dark mode
  shop:       { bg: "#BBF7D0", label: "Shop day" },
  job:        { bg: "var(--cell-job-bg, #FFFFFF)", label: "Job" },          // white / dark card in dark mode
};

const VIEW_DAYS = { day: 1, week: 7, "2week": 14, "3week": 21, month: 28 };
const VIEW_LABELS = { day: "Day", week: "Week", "2week": "2 Wk", "3week": "3 Wk", month: "Month" };

function buildDateRange(start, days) {
  const arr = [];
  for (let i = 0; i < days; i++) arr.push(addDays(start, i));
  return arr;
}

function shortMob(m, projectsMap, scopeMap) {
  const p = projectsMap[m.project_id];
  const jobNo = p?.job_number || "";
  const name = p?.project_name || m.title || "";
  const fullTrade = (m.scope_type || "").split(",")[0].trim();
  // Prefer the real scope CODE (e.g. "TRA") from scope_codes; fall back to first-letter acronym.
  const code =
    (scopeMap && scopeMap[fullTrade.toLowerCase()]) ||
    fullTrade
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 4)
      .toUpperCase() ||
    "";
  // `Job# + Name + CODE` — rendered wrapping via CSS, not truncated.
  const parts = [];
  if (jobNo) parts.push(jobNo);
  if (name) parts.push(name);
  if (code) parts.push(code);
  return parts.join(" · ");
}

// A crew can be any colour the office picked, so the band's lettering follows its brightness
// instead of always being white (pale crews used to read white-on-pale).
const inkOn = (hex) => {
  const h = String(hex || "#cccccc").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h.padEnd(6, "0");
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.179 ? "#111827" : "#ffffff";
};

export default function ScheduleBoard({ projectId }) {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [view, setView] = useState("2week");

  // When user switches to Day view, snap the anchor to TODAY (not the
  // Monday of the current week). All other multi-day views keep the
  // Monday-aligned start so weeks line up.
  const changeView = (v) => {
    setView(v);
    if (v === "day") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setWeekStart(today);
    } else if (view === "day") {
      // leaving Day view — realign to Monday of the current focused day
      setWeekStart(startOfWeek(weekStart));
    }
  };
  const [mobs, setMobs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [crews, setCrews] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dragMobId, setDragMobId] = useState(null);
  // Debounce ref so a double-click can cancel the single-click "open popover"
  // before it fires. Single-click → schedules action in 220ms; double-click →
  // clears the timer and instead highlights the cell for Delete-to-clear.
  const clickTimerRef = React.useRef(null);
  const [cellMenu, setCellMenu] = useState(null); // { installer_id, date, x, y }
  // Edge-copy state: { sourceIso, currentIso, installerId, source: {status_type, mobilization_id, assigned_hours, note, is_lead_assignment} }
  const [copyDrag, setCopyDrag] = useState(null);
  const [popoverMob, setPopoverMob] = useState(null);
  // Multi-select for bulk delete:
  // selectedCells = Set<"installerId|iso">  · anchorCell remembers the last
  // plain-clicked cell so Shift-click can build a range within the same row.
  const [selectedCells, setSelectedCells] = useState(() => new Set());
  const [anchorCell, setAnchorCell] = useState(null); // {installer_id, assigned_date}

  const dates = useMemo(() => buildDateRange(weekStart, VIEW_DAYS[view]), [weekStart, view]);
  const fromDate = fmtIso(dates[0]);
  const toDate = fmtIso(dates[dates.length - 1]);

  const load = async () => {
    setLoading(true);
    try {
      const [mobsR, projR, instR, crewR, asgnR, scopesR] = await Promise.all([
        api.get("/mobilizations", { params: projectId ? { project_id: projectId } : {} }),
        api.get("/projects"),
        api.get("/installers"),
        api.get("/crews"),
        api.get("/assignments", { params: { date_from: fromDate, date_to: toDate } }),
        api.get("/scope-codes"),
      ]);
      Promise.all([api.get("/dispatch/vehicles"), api.get("/dispatch/vehicle-reservations", { params: { date_from: fromDate, date_to: toDate } })])
        .then(([v, r]) => { setVehicles(v.data || []); setReservations(r.data || []); }).catch(() => {});
      setMobs(mobsR.data || []);
      setProjects(projR.data || []);
      setInstallers((instR.data || []).filter((i) => i.active));
      setCrews(crewR.data || []);
      setAssignments(asgnR.data || []);
      setScopes(scopesR.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, fromDate, toDate]);

  const projectsMap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const mobsMap = useMemo(() => Object.fromEntries(mobs.map((m) => [m.id, m])), [mobs]);
  // Trade-name → scope code (lower-case lookup, e.g. "toilet room accessories" → "TRA")
  const scopeMap = useMemo(() => {
    const m = {};
    for (const s of scopes) {
      if (s.trade) m[s.trade.toLowerCase()] = s.code;
      if (s.code) m[s.code.toLowerCase()] = s.code;
    }
    return m;
  }, [scopes]);

  // Mobs awaiting scheduling — status=ready_to_dispatch (scheduler released)
  const mobsToSchedule = useMemo(
    () => mobs.filter((m) => m.status === "ready_to_dispatch"),
    [mobs],
  );

  // Group installers by crew — in the order crews are listed
  const grouped = useMemo(() => {
    const crewOrder = [...crews];
    // Put crews with a foreman name alphabetically, unassigned last
    const out = crewOrder.map((c) => ({
      crew: c,
      members: installers.filter((i) => i.crew_id === c.id),
    }));
    const unassigned = installers.filter((i) => !i.crew_id);
    if (unassigned.length) out.push({ crew: { id: "", name: "Unassigned", color: "#D9D9D9" }, members: unassigned });
    return out.filter((g) => g.members.length > 0);
  }, [crews, installers]);

  // Vehicles ride directly under Warehouse/Delivery (or the last band when there is none)
  const vehicleCrewAfter = (grouped.find((g) => /warehouse|deliver/i.test(g.crew.name)) || grouped[grouped.length - 1])?.crew.id;

  // Assignment lookup: { "installer_id|yyyy-mm-dd" -> assignment }
  const asgnMap = useMemo(() => {
    const m = {};
    for (const a of assignments) m[`${a.installer_id}|${a.assigned_date}`] = a;
    return m;
  }, [assignments]);

  // Standing-assignment helper — show light-cream when installer has standing + no real assignment
  const cellFor = (installerId, dateIso, installer) => {
    const real = asgnMap[`${installerId}|${dateIso}`];
    if (real) return real;
    if (installer.standing_assignment) {
      return {
        _standing: true,
        status_type: "warehouse",
        note: installer.standing_assignment,
      };
    }
    return null;
  };

  const prev = () => setWeekStart((s) => addDays(s, -VIEW_DAYS[view]));
  const next = () => setWeekStart((s) => addDays(s, VIEW_DAYS[view]));
  const today = () => {
    // In Day view, "Today" jumps to the current date. Every other view
    // realigns to the Monday of the current week.
    if (view === "day") {
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      setWeekStart(t);
    } else {
      setWeekStart(startOfWeek(new Date()));
    }
  };

  const upsertCell = async (installer_id, assigned_date, data) => {
    try {
      await api.post("/assignments/upsert", { installer_id, assigned_date, ...data });
      load();
    } catch { toast.error("Could not save"); }
  };
  const clearCell = async (installer_id, assigned_date) => {
    try {
      await api.delete("/assignments/cell", { params: { installer_id, assigned_date } });
      load();
    } catch { toast.error("Could not clear"); }
  };

  // Drag mob onto cell
  const onCellDrop = async (e, installer_id, dateIso) => {
    e.preventDefault();
    // Detect a cell→cell move first: the dragstart on a cell sets the JSON
    // payload `application/x-fieldops-cell` with the source { installerId,
    // iso, assignmentId, snapshot }. When present we MOVE the assignment
    // (delete source, upsert target) instead of cloning a pool mob.
    const cellPayload = e.dataTransfer?.getData("application/x-fieldops-cell") || "";
    if (cellPayload) {
      let src;
      try { src = JSON.parse(cellPayload); } catch { src = null; }
      if (!src) return;
      if (src.installerId === installer_id && src.iso === dateIso) return;  // same cell — nothing to do
      // If the target cell currently has another assignment, the upsert
      // will replace it. That mirrors the existing pool-drop behavior.
      try {
        await api.post("/assignments/upsert", {
          installer_id,
          assigned_date: dateIso,
          status_type: src.status_type,
          mobilization_id: src.mobilization_id || "",
          assigned_hours: src.assigned_hours || 8,
          note: src.note || "",
          is_lead_assignment: !!src.is_lead_assignment,
        });
        await api.delete("/assignments/cell", { params: { installer_id: src.installerId, assigned_date: src.iso } });
        toast.success("Moved");
        load();
      } catch {
        toast.error("Move failed");
      }
      return;
    }
    if (!dragMobId) return;
    const mob = mobsMap[dragMobId];
    if (!mob) return;
    upsertCell(installer_id, dateIso, {
      status_type: "job",
      mobilization_id: dragMobId,
      assigned_hours: 8,
    });
    setDragMobId(null);
  };

  // ---- Keyboard-delete on the currently selected cell(s) ----
  // If multiple cells are selected, all are cleared in parallel. After the
  // bulk delete, any mobs whose last assignment was just removed flip back to
  // "scheduled" so they re-appear on the Schedule board (not the Dispatch
  // Pool — drag onto the pool sidebar to do that).
  const bulkClear = async (cellKeys) => {
    if (!cellKeys || cellKeys.length === 0) return;
    const toClear = cellKeys.map((k) => {
      const [installer_id, assigned_date] = k.split("|");
      return { installer_id, assigned_date };
    });
    const mobIds = new Set();
    for (const c of toClear) {
      const a = assignments.find(
        (x) => x.installer_id === c.installer_id && x.assigned_date === c.assigned_date,
      );
      if (a?.mobilization_id) mobIds.add(a.mobilization_id);
    }
    try {
      await Promise.all(
        toClear.map((c) =>
          api.delete("/assignments/cell", {
            params: { installer_id: c.installer_id, assigned_date: c.assigned_date },
          }),
        ),
      );
      // For each affected mob, if zero assignments remain, flip status back.
      await Promise.all(
        [...mobIds].map(async (mid) => {
          try {
            const { data: rest } = await api.get("/assignments", { params: { mobilization_id: mid } });
            if (!rest || rest.length === 0) {
              await api.patch(`/mobilizations/${mid}`, { status: "scheduled" });
            }
          } catch { /* best-effort */ }
        }),
      );
      const n = toClear.length;
      toast.success(`Cleared ${n} cell${n === 1 ? "" : "s"}`);
      setSelectedCells(new Set());
      setAnchorCell(null);
      setCellMenu(null);
      load();
    } catch {
      toast.error("Could not clear");
    }
  };

  useEffect(() => {
    const onKey = async (e) => {
      if (e.key === "Escape") {
        if (selectedCells.size > 0) {
          setSelectedCells(new Set());
          setAnchorCell(null);
        }
        return;
      }
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (selectedCells.size === 0) return;
      e.preventDefault();
      await bulkClear([...selectedCells]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedCells, assignments]); // eslint-disable-line
  useEffect(() => {
    if (!copyDrag) return;
    const onMove = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el) return;
      const td = el.closest("td[data-cell-iso]");
      if (!td) return;
      const iso = td.getAttribute("data-cell-iso");
      const installerId = td.getAttribute("data-cell-installer-id");
      // Only allow copying within the same installer's row.
      if (!iso || installerId !== copyDrag.installerId) return;
      if (iso !== copyDrag.currentIso) setCopyDrag((c) => ({ ...c, currentIso: iso }));
    };
    const onUp = async () => {
      const c = copyDrag;
      setCopyDrag(null);
      if (!c) return;
      const a = c.sourceIso < c.currentIso ? c.sourceIso : c.currentIso;
      const b = c.sourceIso > c.currentIso ? c.sourceIso : c.currentIso;
      if (a === b) return; // user released on the source cell — nothing to copy
      const days = [];
      for (let cur = new Date(a); fmtIso(cur) <= b; cur = addDays(cur, 1)) days.push(fmtIso(cur));
      const targets = days.filter((d) => d !== c.sourceIso);
      try {
        await Promise.all(
          targets.map((d) =>
            api.post("/assignments/upsert", {
              installer_id: c.installerId,
              assigned_date: d,
              status_type: c.source.status_type,
              mobilization_id: c.source.mobilization_id || "",
              assigned_hours: c.source.assigned_hours || 8,
              note: c.source.note || "",
              is_lead_assignment: !!c.source.is_lead_assignment,
            })
          )
        );
        toast.success(`Copied to ${targets.length} day${targets.length > 1 ? "s" : ""}`);
        load();
      } catch { toast.error("Could not copy"); }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [copyDrag]); // eslint-disable-line

  // Footer totals — unique installers working a "job" on any day in view
  const footerTotals = useMemo(() => {
    const byRole = { div10: new Set(), glass: new Set(), opw: new Set(), warehouse: new Set(), foremen: new Set(), total: new Set() };
    for (const a of assignments) {
      const inst = installers.find((i) => i.id === a.installer_id);
      if (!inst) continue;
      byRole.total.add(inst.id);
      const mob = mobsMap[a.mobilization_id];
      const scope = (mob?.scope_type || "").toLowerCase();
      if (inst.role === "Foreman") byRole.foremen.add(inst.id);
      if (inst.role === "Warehouse") byRole.warehouse.add(inst.id);
      if (scope.includes("storefront") || scope.includes("glazing")) byRole.glass.add(inst.id);
      else if (scope.includes("operable")) byRole.opw.add(inst.id);
      else if (scope) byRole.div10.add(inst.id);
    }
    return Object.fromEntries(Object.entries(byRole).map(([k, v]) => [k, v.size]));
  }, [assignments, installers, mobsMap]);

  // Per-day booked / budgeted hours bar.
  // booked = sum of assigned_hours on every JOB assignment on that date
  // budget = count of active Installers + Leads × 10 (matches the user's
  // 10h workday standard). Foremen are excluded from the budgeted pool.
  const hoursPerDay = useMemo(() => {
    const installerCount = installers.filter((i) => (i.active !== false) && (i.role === "Installer" || i.role === "Lead" || i.is_lead)).length;
    const dailyBudget = installerCount * 10;
    const map = {};
    for (const d of dates) {
      const iso = fmtIso(d);
      map[iso] = { booked: 0, budget: dailyBudget };
    }
    for (const a of assignments) {
      if (a.status_type !== "job") continue;
      if (!map[a.assigned_date]) continue;
      map[a.assigned_date].booked += Number(a.assigned_hours) || 0;
    }
    return map;
  }, [dates, assignments, installers]);

  const mobSearchFiltered = mobsToSchedule.filter((m) => {
    if (!search) return true;
    const p = projectsMap[m.project_id];
    const blob = [p?.project_name, p?.job_number, m.scope_type, m.title, m.building].join(" ").toLowerCase();
    return blob.includes(search.toLowerCase());
  });

  return (
    <div className="flex h-[calc(100vh-200px)] min-h-[640px] gap-3">
      {/* -------- Left panel: mobilizations to schedule -------- */}
      <aside
        className="w-[260px] shrink-0 border border-gray-200 bg-white rounded-lg flex flex-col overflow-hidden"
        onDragOver={(e) => {
          // Allow cell→pool drops only. Pool→pool is a no-op.
          if (e.dataTransfer && Array.from(e.dataTransfer.types).includes("application/x-fieldops-cell")) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
          }
        }}
        onDrop={async (e) => {
          const cellPayload = e.dataTransfer?.getData("application/x-fieldops-cell") || "";
          if (!cellPayload) return;
          let src;
          try { src = JSON.parse(cellPayload); } catch { return; }
          if (!src) return;
          // Job assignments only — time-off cells have no mob to return.
          if (src.status_type !== "job" || !src.mobilization_id) {
            toast.error("Only job assignments can return to the pool");
            return;
          }
          try {
            // Wipe ALL assignments tied to this mob (every installer, every
            // day). The pool drop = "send entire mob back to dispatch pool".
            const { data: rels } = await api.get("/assignments", { params: { mobilization_id: src.mobilization_id } });
            await Promise.all((rels || []).map((a) =>
              api.delete("/assignments/cell", { params: { installer_id: a.installer_id, assigned_date: a.assigned_date } })
            ));
            await api.patch(`/mobilizations/${src.mobilization_id}`, { status: "ready_to_dispatch" });
            toast.success(`Sent back to Dispatch Pool · ${(rels || []).length} day${(rels || []).length === 1 ? "" : "s"} cleared`);
            load();
          } catch {
            toast.error("Return-to-pool failed");
          }
        }}
        data-testid="sched-pool-droparea"
      >
        <header className="px-3 py-2 border-b border-gray-200 bg-gray-50/60">
          <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Dispatch Pool</h3>
          <p className="text-[11px] text-gray-500">{mobsToSchedule.length} ready · drag onto a cell</p>
        </header>
        <div className="p-2 border-b border-gray-100">
          <Input data-testid="sched-mob-search" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-7 text-xs" />
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
          {mobSearchFiltered.length === 0 && <div className="text-xs text-gray-400 text-center py-10">No mobs to schedule.</div>}
          {mobSearchFiltered.map((m) => {
            const p = projectsMap[m.project_id];
            const { title: cardTitle, units: smartUnits } = smartTitleFromMob(m, scopeMap);
            const projName = p?.project_name || m.title || "";
            const projPrefix = projName.slice(0, 3);
            const projRest = projName.slice(3);
            return (
              <div
                key={m.id}
                draggable
                onDragStart={() => setDragMobId(m.id)}
                onDragEnd={() => setDragMobId(null)}
                onClick={() => setPopoverMob(m)}
                data-testid={`sched-pool-mob-${m.id}`}
                className={`border rounded-md p-2 text-xs hover:shadow-sm cursor-grab active:cursor-grabbing ${
                  m.is_punchlist ? "bg-orange-50 border-orange-300 hover:border-orange-500" : "bg-white border-gray-200 hover:border-blue-400"
                }`}
              >
                <div className="font-semibold truncate" title={projName}>
                  {p?.id ? (
                    <>
                      <Link
                        to={`/projects/${p.id}`}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="text-blue-700 hover:text-blue-900 hover:underline"
                        title="Open job · FRS"
                        data-testid={`sched-pool-link-${m.id}`}
                      >
                        {projPrefix}
                      </Link>
                      <span className="text-gray-900">{projRest}</span>
                    </>
                  ) : (
                    <span className="text-gray-900">{projName}</span>
                  )}
                  {m.is_punchlist && <span className="ml-1 text-[8px] uppercase font-bold text-orange-700 align-middle">P</span>}
                </div>
                {p?.job_number && <div className="text-[10px] text-gray-500">Job #{p.job_number}</div>}
                <div className="font-mono text-[11px] font-semibold text-gray-900 truncate" data-testid={`sched-pool-title-${m.id}`}>{cardTitle}</div>
                {smartUnits && <div className="text-[10px] text-gray-500" data-testid={`sched-pool-units-${m.id}`}>{smartUnits}</div>}
                <div className="flex justify-end items-center mt-1 text-[10px] text-gray-500">
                  <span className="font-medium">{m.num_installers || 1} Inst</span>
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      {/* -------- Main schedule grid -------- */}
      <div className="flex-1 border border-gray-200 bg-white rounded-lg flex flex-col overflow-hidden">
        {/* Top toolbar */}
        <header className="px-3 py-2 border-b border-gray-200 bg-gray-50/60 flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={prev} className="h-7 w-7 p-0"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={today} className="h-7 text-xs">Today</Button>
          <Button variant="outline" size="sm" onClick={next} className="h-7 w-7 p-0"><ChevronRight className="w-4 h-4" /></Button>
          <span className="text-sm font-medium text-gray-800 ml-1">
            {dates[0].toLocaleDateString("en", { month: "short", day: "numeric" })} – {dates[dates.length - 1].toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" })}
          </span>
          {selectedCells.size > 0 && (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700 text-[11px] font-medium"
              data-testid="sched-selection-count"
              title="Press Delete or double-click a non-job cell to clear · Esc to deselect"
            >
              <span>{selectedCells.size} selected</span>
              <button
                type="button"
                onClick={() => bulkClear([...selectedCells])}
                data-testid="sched-bulk-delete-btn"
                className="ml-1 px-1.5 py-0 rounded text-[10px] font-bold bg-red-600 text-white hover:bg-red-700"
                title="Delete all selected (Delete key)"
              >Delete</button>
              <button
                type="button"
                onClick={() => { setSelectedCells(new Set()); setAnchorCell(null); }}
                data-testid="sched-selection-clear-btn"
                className="text-red-500 hover:text-red-700"
                title="Clear selection"
              ><X className="w-3 h-3" /></button>
            </span>
          )}
          <div className="ml-auto inline-flex items-center border border-gray-300 rounded-md bg-white overflow-hidden" data-testid="sched-view-toggle">
            {Object.keys(VIEW_DAYS).map((v) => (
              <button
                key={v}
                data-testid={`sched-view-${v}`}
                onClick={() => changeView(v)}
                className={`px-2 h-7 text-xs border-l border-gray-300 first:border-l-0 ${view === v ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"}`}
              >{VIEW_LABELS[v]}</button>
            ))}
          </div>
        </header>

        {/* Grid body */}
        <div className="flex-1 overflow-auto relative" data-testid="sched-grid-scroll">
          <table className="text-xs border-collapse">
            <thead className="sticky top-0 z-20">
              <tr>
                <th className="bg-gray-100 border-b border-r border-gray-200 text-left px-2 py-1.5" style={{ position: "sticky", left: 0, minWidth: 220, zIndex: 25 }}>
                  <span className="text-[10px] uppercase text-gray-500 font-medium">Installer</span>
                </th>
                {dates.map((d) => {
                  const iso = fmtIso(d);
                  const wk = isWeekend(d);
                  const h = hoursPerDay[iso] || { booked: 0, budget: 0 };
                  const over = h.budget > 0 && h.booked > h.budget;
                  const pct = h.budget > 0 ? Math.min(100, (h.booked / h.budget) * 100) : 0;
                  return (
                    <th key={iso} className={`border-b border-r border-gray-200 px-1 py-1.5 text-center font-medium ${wk ? "bg-gray-200 text-gray-700" : "bg-gray-50 text-gray-700"}`} style={{ minWidth: 120, maxWidth: 160 }}>
                      <div className="text-[10px] uppercase tracking-wider">{SHORT_DAY[d.getDay()]}</div>
                      <div className="text-[11px]">{d.getMonth() + 1}/{d.getDate()}</div>
                      {/* Booked / budgeted hours bar — mirrors ScheduleBoardV2's per-day rollup. */}
                      {h.budget > 0 && (
                        <div
                          className="mt-1 mx-1 flex flex-col items-stretch"
                          title={`${Math.round(h.booked)}h booked of ${h.budget}h budgeted`}
                          data-testid={`sched-day-hours-${iso}`}
                        >
                          <div className={`text-[9px] tabular-nums leading-3 ${over ? "text-red-600 font-semibold" : "text-gray-500"}`}>
                            {Math.round(h.booked)}/{h.budget}h
                          </div>
                          <div className="h-1 mt-0.5 rounded bg-gray-200 overflow-hidden">
                            <div
                              className={`h-full ${over ? "bg-red-500" : pct >= 80 ? "bg-amber-400" : "bg-emerald-400"}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => (
                <React.Fragment key={g.crew.id || "unassigned"}>
                  {/* Crew band */}
                  <tr data-testid={`sched-crew-band-${g.crew.id || "unassigned"}`}>
                    <td colSpan={dates.length + 1} className="border-b border-gray-200 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide" style={{ background: g.crew.color || "#ccc", color: inkOn(g.crew.color) }}>
                      {g.crew.name} · {g.members.length}
                    </td>
                  </tr>
                  {g.members.map((inst) => (
                    <tr key={inst.id} className="hover:bg-blue-50/30">
                      <td className="border-b border-r border-gray-200 px-2 py-1.5 bg-white" style={{ position: "sticky", left: 0, zIndex: 5 }}>
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-sm" style={{ background: g.crew.color || "#ccc" }}></span>
                          <div className="leading-tight">
                            <div className="text-[12px] font-medium text-gray-900 truncate" title={inst.name}>{inst.name}</div>
                            <div className="text-[10px] text-gray-500">{inst.role}</div>
                          </div>
                        </div>
                      </td>
                      {dates.map((d) => {
                        const iso = fmtIso(d);
                        const wk = isWeekend(d);
                        const a = cellFor(inst.id, iso, inst);
                        const style = {};
                        // Cells that keep a LIGHT pastel fill even in dark
                        // mode need dark lettering (class `sched-tinted`).
                        let lightTint = false;
                        if (wk && !a) {
                          style.background = STATUS_STYLE.holiday.bg;
                        } else if (a) {
                          const st = STATUS_STYLE[a.status_type] || STATUS_STYLE.job;
                          style.background = st.bg;
                          lightTint = a.status_type !== "job";
                          // Punchlist tint overrides job color (light orange)
                          if (a.status_type === "job" && a.mobilization_id) {
                            const _mob = mobsMap[a.mobilization_id];
                            if (_mob && _mob.is_punchlist) { style.background = "#FFE4C4"; lightTint = true; } // bisque/light orange
                          }
                          // Standing-assignment "ghost" treatment: faded fill +
                          // dashed border + reduced opacity so the eye reads it
                          // as a default, not a confirmed booking.
                          if (a._standing) {
                            style.background = "repeating-linear-gradient(45deg, #fdf6e3 0 6px, #f5ecc8 6px 12px)";
                            style.opacity = 0.7;
                            style.borderStyle = "dashed";
                            style.borderColor = "#cbb279";
                            lightTint = true;
                          }
                        }
                        let mob = null;
                        if (a && a.status_type === "job" && a.mobilization_id) {
                          mob = mobsMap[a.mobilization_id] || null;
                        }
                        let content = "";
                        if (a) {
                          if (a.status_type === "job") {
                            content = mob ? shortMob(mob, projectsMap, scopeMap) : (a.note || "");
                          } else if (a.note) {
                            content = a.note;
                          } else {
                            content = STATUS_STYLE[a.status_type]?.label || a.status_type;
                          }
                        }
                        const testKey = `sched-cell-${inst.id}-${iso}`;
                        // Live highlight while user is edge-copying within this installer's row
                        const inCopy =
                          copyDrag &&
                          copyDrag.installerId === inst.id &&
                          ((iso > copyDrag.sourceIso && iso <= copyDrag.currentIso) ||
                            (iso < copyDrag.sourceIso && iso >= copyDrag.currentIso));
                        const cellKey = `${inst.id}|${iso}`;
                        const isSelected = selectedCells.has(cellKey);
                        return (
                          <td
                            key={iso}
                            data-testid={testKey}
                            data-cell-iso={iso}
                            data-cell-installer-id={inst.id}
                            draggable={!!a}
                            onDragStart={(e) => {
                              if (!a) return;
                              // Cell-to-cell move payload. Includes the
                              // current source's snapshot so the target
                              // upsert reproduces the exact card.
                              e.dataTransfer.effectAllowed = "move";
                              e.dataTransfer.setData(
                                "application/x-fieldops-cell",
                                JSON.stringify({
                                  installerId: inst.id,
                                  iso,
                                  assignmentId: a.id,
                                  status_type: a.status_type,
                                  mobilization_id: a.mobilization_id || "",
                                  assigned_hours: a.assigned_hours || 8,
                                  note: a.note || "",
                                  is_lead_assignment: !!a.is_lead_assignment,
                                })
                              );
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => onCellDrop(e, inst.id, iso)}
                            onDoubleClick={(e) => {
                              // Empty cell → open status menu (PTO / Travel /
                              // Training / etc.). Filled NON-job cell (PTO,
                              // sick, personal, training, etc.) → clear
                              // immediately. Filled JOB cell → just stay
                              // selected so user can confirm with another
                              // gesture (delete key or click pool).
                              e.preventDefault();
                              e.stopPropagation();
                              if (clickTimerRef.current) {
                                clearTimeout(clickTimerRef.current);
                                clickTimerRef.current = null;
                              }
                              setPopoverMob(null);
                              if (!a || a._standing) {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setCellMenu({ installer_id: inst.id, date: iso, x: rect.left, y: rect.bottom });
                                setSelectedCells(new Set([cellKey]));
                                setAnchorCell({ installer_id: inst.id, assigned_date: iso });
                                return;
                              }
                              setCellMenu(null);
                              if (a.status_type !== "job") {
                                // Quick-erase any time-off / non-job entry.
                                bulkClear([cellKey]);
                              } else {
                                // Job cells: select for review; explicit
                                // Delete key still works.
                                setSelectedCells(new Set([cellKey]));
                                setAnchorCell({ installer_id: inst.id, assigned_date: iso });
                              }
                            }}
                            onClick={(e) => {
                              if (copyDrag) return;
                              const isShift = e.shiftKey;
                              const isToggle = e.metaKey || e.ctrlKey;

                              // Cmd/Ctrl-click → toggle this cell in selection
                              if (isToggle) {
                                e.preventDefault();
                                e.stopPropagation();
                                if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
                                setSelectedCells((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(cellKey)) next.delete(cellKey);
                                  else next.add(cellKey);
                                  return next;
                                });
                                setAnchorCell({ installer_id: inst.id, assigned_date: iso });
                                setPopoverMob(null);
                                return;
                              }

                              // Shift-click → range-select within the same
                              // installer row from the anchor cell.
                              if (isShift && anchorCell && anchorCell.installer_id === inst.id) {
                                e.preventDefault();
                                e.stopPropagation();
                                if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
                                const a0 = anchorCell.assigned_date;
                                const lo = a0 < iso ? a0 : iso;
                                const hi = a0 > iso ? a0 : iso;
                                const next = new Set(selectedCells);
                                for (const d of dates) {
                                  const di = fmtIso(d);
                                  if (di >= lo && di <= hi) next.add(`${inst.id}|${di}`);
                                }
                                setSelectedCells(next);
                                setPopoverMob(null);
                                return;
                              }

                              // Plain click — replace selection with this single cell.
                              if (!a) {
                                setSelectedCells(new Set([cellKey]));
                                setAnchorCell({ installer_id: inst.id, assigned_date: iso });
                                return;
                              }
                              if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
                              clickTimerRef.current = setTimeout(() => {
                                clickTimerRef.current = null;
                                if (a.mobilization_id) {
                                  const mob = mobs.find((m) => m.id === a.mobilization_id);
                                  if (mob) {
                                    setPopoverMob(mob);
                                    return;
                                  }
                                }
                                setSelectedCells(new Set([cellKey]));
                                setAnchorCell({ installer_id: inst.id, assigned_date: iso });
                              }, 220);
                            }}
                            className={`border-b border-r border-gray-200 px-1 py-1 text-[10px] text-gray-700 cursor-pointer align-top relative group ${lightTint ? "sched-tinted" : ""}`}
                            style={{
                              ...style,
                              minWidth: 147,
                              maxWidth: 200,
                              minHeight: 48,
                              outline: inCopy
                                ? "2px solid rgba(59,130,246,0.6)"
                                : isSelected
                                ? "2px solid rgba(220,38,38,0.7)"
                                : "none",
                              outlineOffset: -2,
                            }}
                            title={content + (isSelected ? ` · press Delete to clear${selectedCells.size > 1 ? ` ${selectedCells.size} cells` : ""}` : "")}
                          >
                            {/* Rich card for job assignments — mirrors the
                                schedule-board pool/cell visual: linked
                                project name, scope code badge, building/
                                floor/rooms, hours, lead pill. Falls back to
                                the simple text for time-off / notes. */}
                            {a && a.status_type === "job" && mob ? (
                              <div className="leading-tight">
                                <div className="flex items-center gap-1 min-w-0">
                                  {projectsMap[mob.project_id]?.id ? (() => {
                                    const projName = projectsMap[mob.project_id]?.project_name || mob.title || "";
                                    const projPrefix = projName.slice(0, 3);
                                    const projRest = projName.slice(3);
                                    return (
                                      <span className="text-[11px] font-semibold truncate flex-1" title={projName}>
                                        <Link
                                          to={`/projects/${mob.project_id}`}
                                          onClick={(e) => e.stopPropagation()}
                                          onMouseDown={(e) => e.stopPropagation()}
                                          className="text-blue-700 hover:text-blue-900 hover:underline"
                                          data-testid={`dispatch-cell-link-${a.id}`}
                                        >
                                          {projPrefix}
                                        </Link>
                                        <span className="text-gray-900">{projRest}</span>
                                      </span>
                                    );
                                  })() : (
                                    <span className="text-[11px] font-semibold text-gray-900 truncate flex-1">{mob.title}</span>
                                  )}
                                  <Link
                                    to={`/install?project=${mob.project_id}&mob=${mob.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    title="Open the Install FRS for this dispatch"
                                    className="text-[8px] uppercase font-bold text-orange-700 bg-orange-50 border border-orange-200 rounded px-1 leading-3 shrink-0 hover:bg-orange-100"
                                    data-testid={`dispatch-cell-open-frs-${a.id}`}
                                  >
                                    FRS
                                  </Link>
                                  {a.is_lead_assignment && (                                    <span className="text-[8px] uppercase font-bold text-purple-700 bg-purple-50 border border-purple-200 rounded px-1 leading-3 shrink-0" title="Lead">L</span>
                                  )}
                                  {mob.is_punchlist && (
                                    <span className="text-[8px] uppercase font-bold text-orange-700 bg-orange-100 border border-orange-300 rounded px-1 leading-3 shrink-0" title="Punchlist">P</span>
                                  )}
                                </div>
                                {(() => {
                                  const { title: cardTitle, units: smartUnits } = smartTitleFromMob(mob, scopeMap);
                                  return (
                                    <>
                                      <div className="flex items-center justify-between text-[9px] mt-0.5">
                                        <span className="font-mono font-semibold text-gray-900 truncate" data-testid={`dispatch-cell-title-${a.id}`}>{cardTitle}</span>
                                        <span className="tabular-nums text-gray-600">{Math.round(Number(a.assigned_hours) || 8)}h</span>
                                      </div>
                                      {smartUnits && (
                                        <div className="text-[9px] text-gray-500 truncate" data-testid={`dispatch-cell-units-${a.id}`}>{smartUnits}</div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            ) : (
                              <div
                                className="font-medium pr-2 leading-tight"
                                style={{ wordBreak: "normal", overflowWrap: "break-word" }}
                              >
                                {content}
                              </div>
                            )}
                            {a?._standing && (
                              <div className="text-[8px] italic text-amber-700 font-semibold uppercase tracking-wider mt-0.5" data-testid={`sched-standing-${inst.id}-${iso}`}>
                                ⌁ standing
                              </div>
                            )}
                            {/* Right-edge copy-drag handle — visible only on filled (non-standing) cells */}
                            {a && !a._standing && (
                              <div
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setCopyDrag({
                                    installerId: inst.id,
                                    sourceIso: iso,
                                    currentIso: iso,
                                    source: {
                                      status_type: a.status_type,
                                      mobilization_id: a.mobilization_id || "",
                                      assigned_hours: a.assigned_hours || 8,
                                      note: a.note || "",
                                      is_lead_assignment: !!a.is_lead_assignment,
                                    },
                                  });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`sched-copy-handle-${inst.id}-${iso}`}
                                title="Drag right to copy across days"
                                className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-blue-500/0 hover:bg-blue-500/40 group-hover:bg-blue-500/20"
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {g.crew.id === vehicleCrewAfter && (
                    <VehicleCrewRows vehicles={vehicles} reservations={reservations} dates={dates} fmtIso={fmtIso}
                                     isWeekend={isWeekend} projects={projects} onChanged={load} />
                  )}
                </React.Fragment>
              ))}
              {grouped.length === 0 && !loading && (
                <tr><td colSpan={dates.length + 1} className="px-3 py-16 text-center text-gray-500">No installers. Seed the team in Settings → Data.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer totals */}
        <footer className="px-3 py-1.5 border-t border-gray-200 bg-gray-50/60 flex items-center gap-4 text-xs text-gray-600 flex-wrap" data-testid="sched-footer-totals">
          <span><span className="font-semibold text-gray-800">DIV 10:</span> {footerTotals.div10}</span>
          <span><span className="font-semibold text-gray-800">Glass:</span> {footerTotals.glass}</span>
          <span><span className="font-semibold text-gray-800">Op wall/Service:</span> {footerTotals.opw}</span>
          <span><span className="font-semibold text-gray-800">Delivery/warehouse:</span> {footerTotals.warehouse}</span>
          <span><span className="font-semibold text-gray-800">Foremen:</span> {footerTotals.foremen}</span>
          <span className="ml-auto font-semibold text-gray-900">Total: {footerTotals.total}</span>
        </footer>
      </div>

      {/* -------- Cell menu (PTO/travel/training/warehouse/clear) -------- */}
      {cellMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setCellMenu(null)}>
          <div
            className="absolute bg-white border border-gray-200 shadow-lg rounded-md text-xs"
            style={{ top: cellMenu.y + 4, left: Math.min(cellMenu.x, window.innerWidth - 200) }}
            onClick={(e) => e.stopPropagation()}
          >
            {[
              ["pto", STATUS_STYLE.pto.label, STATUS_STYLE.pto.bg],
              ["sick", STATUS_STYLE.sick.label, STATUS_STYLE.sick.bg],
              ["personal", STATUS_STYLE.personal.label, STATUS_STYLE.personal.bg],
              ["training", STATUS_STYLE.training.label, STATUS_STYLE.training.bg],
              ["holiday", STATUS_STYLE.holiday.label, STATUS_STYLE.holiday.bg],
            ].map(([type, label, bg]) => (
              <button
                key={type}
                data-testid={`sched-menu-${type}`}
                className="block w-48 text-left px-3 py-1.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                onClick={() => {
                  upsertCell(cellMenu.installer_id, cellMenu.date, { status_type: type, note: label });
                  setCellMenu(null);
                }}
              >
                <span className="inline-block w-3 h-3 rounded-sm mr-2 align-middle border border-gray-300" style={{ background: bg }}></span>
                {label}
              </button>
            ))}
            <button
              data-testid="sched-menu-clear"
              className="block w-48 text-left px-3 py-1.5 hover:bg-red-50 text-red-600 border-t border-gray-200"
              onClick={() => {
                clearCell(cellMenu.installer_id, cellMenu.date);
                setCellMenu(null);
              }}
            >
              <X className="w-3 h-3 inline mr-1.5" /> Clear cell
            </button>
          </div>
        </div>
      )}

      {/* Detailed mob popover (opens on single-click of any cell with a mob) */}
      <MobPopover
        open={!!popoverMob}
        onOpenChange={(o) => { if (!o) setPopoverMob(null); }}
        mob={popoverMob}
        project={popoverMob ? projectsMap[popoverMob.project_id] : null}
        scopeCode={popoverMob ? (scopeMap[(popoverMob.scope_type || "").toLowerCase()] || (popoverMob.scope_type || "").slice(0, 3).toUpperCase()) : ""}
        onChanged={(patch) => {
          if (!popoverMob) return;
          setMobs((cur) => cur.map((m) => m.id === popoverMob.id ? { ...m, ...patch } : m));
          setPopoverMob((cur) => cur ? { ...cur, ...patch } : cur);
        }}
      />
    </div>
  );
}
